import useSpeciesData from "hooks/useSpeciesData";
import { getSpecieVolume } from "@foresting-tomorrow/simulation";
import { Fragment, useEffect, useMemo, useState } from "react";
import { ActivityThinningObject } from "types/stand";
import SingleThinningLine from "./singleThinningLine";
import { EstimationModels } from "types/simulation";
import { MapDataFeatures, MapDataStandData, MapDataThinning } from "types/mapData";
import { Box, Chip, Divider, Grid } from "@mui/material";
import { generateUUID } from "utils/deviceTools";
import { useTranslation } from "utils/locales/utilityFunctions";

type ThinningObjectProps = {
  references: { id: string; label: string }[];
  activityId: string;
  projectId: string;
  features: MapDataFeatures;
  selectedFeature: any;
  standData: MapDataStandData;
  thinningData: MapDataThinning;
  removedStandIds: string[];
  nonReferencedSpecies?: string[];
  disabled?: boolean;
  setUpdatedThinningData: (
    data: ActivityThinningObject[],
    type: "referenced" | "non-referenced",
    userChange?: boolean,
  ) => void;
  deleteThinning: (id: string, standId: string) => void;
};

const ThinningObject = ({
  references,
  activityId,
  projectId,
  features,
  selectedFeature,
  standData,
  thinningData,
  removedStandIds,
  nonReferencedSpecies,
  disabled,
  setUpdatedThinningData,
  deleteThinning,
}: ThinningObjectProps) => {
  // --- Hooks --- //
  const { getSpeciesNameAndAbbreviation, getSpeciesModelType, getSpeciesColor, getSpeciesOptions } =
    useSpeciesData();
  const { translate } = useTranslation();

  // const speciesOptions = useMemo(() => {
  //   return getSpeciesOptions({withoutHeaders: true});
  // }, [translate("render-change")]);
  const speciesOptions = getSpeciesOptions({withoutHeaders: true});

  // --- State and refs --- //
  const [thinningState, setThinningState] = useState<{
    data: ActivityThinningObject[];
    userChange: boolean;
  }>({ data: [], userChange: false });
  const actThinningData = thinningState.data;
  const setActThinningData = (data: ActivityThinningObject[], userChange = false) => {
    setThinningState({ data, userChange });
  };

  // --- Effects --- //
  const newThinningData = useMemo(() => {
    if (references.length === 0 || Object.keys(standData).length === 0) return [];
    return getThinningDataFromStandData({
      activityId,
      projectId,
      standData,
      features,
      selectedFeature,
      thinningData,
      references,
      removedStandIds,
      getSpeciesModelType,
      getSpeciesNameAndAbbreviation,
      getSpeciesColor,
      getSpecieVolume,
    });
  }, [references, thinningData, features, standData, removedStandIds]);

  const type = newThinningData.length > 0 ? "referenced" : "non-referenced";

  useEffect(() => {
    // If there are actual references to stand data use this
    if (newThinningData.length > 0) {
      setActThinningData(newThinningData);
      return;
    }
    // Else check for non-referenced species data
    let newData: ActivityThinningObject[] = [];
    if (nonReferencedSpecies && nonReferencedSpecies.length > 0) {
      newData = mapNonReferencedSpeciesToThinningData({
        nonReferencedSpecies,
        references,
        getSpeciesNameAndAbbreviation,
        getSpeciesColor,
        translate,
      });
    }
    const newEl = mapNonReferencedSpeciesToThinningData({
      nonReferencedSpecies: [""],
      references,
      getSpeciesNameAndAbbreviation,
      getSpeciesColor,
      translate,
    });
    newData = [...newData, ...newEl];
    setActThinningData(newData);
  }, [newThinningData]);

  useEffect(() => {
    setUpdatedThinningData(
      actThinningData.filter((el) => el.speciesId !== ""),
      type,
      thinningState.userChange,
    );
  }, [thinningState]);

  // --- Functions --- //
  const handleChangeThinninggPercentage = (
    percentage: number,
    standId: string,
    thinningId?: string
  ) => {
    const newThinningData = actThinningData.map((thinning) => {
      if (thinning.thinningId === thinningId) {
        return { ...thinning, thinningPercentage: percentage };
      }
      return thinning;
    });
    setActThinningData(newThinningData, true);
  };

  const handleChangeSpecies = (speciesId: string, thinningId: string) => {
    let newThinningData = actThinningData.map((thinning) => {
      if (thinning.thinningId === thinningId) {
        return { ...thinning, speciesId, disabled: false };
      }
      return thinning;
    });
    // If all speciesId's are different from "non", add new element to the array
    if (
      newThinningData.every((thinning) => thinning.speciesId !== "" && thinning.speciesId !== "non")
    ) {
      const newEl = mapNonReferencedSpeciesToThinningData({
        nonReferencedSpecies: [""],
        references,
        getSpeciesNameAndAbbreviation,
        getSpeciesColor,
        translate,
      });
      newThinningData = [...newThinningData, ...newEl];
    }
    setActThinningData(newThinningData, true);
  };

  const handleDeleteNonReferencedSpecies = (thinningId: string) => {
    let newThinningData = actThinningData.filter(
      (thinning) => thinning.thinningId !== thinningId
    );
    if (
      newThinningData.length === 0 ||
      newThinningData.every((thinning) => thinning.speciesId !== "" && thinning.speciesId !== "non")
    ) {
      const newEl = mapNonReferencedSpeciesToThinningData({
        nonReferencedSpecies: [""],
        references,
        getSpeciesNameAndAbbreviation,
        getSpeciesColor,
        translate,
      });
      newThinningData = [...newThinningData, ...newEl];
    }
    setActThinningData(newThinningData, true);
  };

  // --- Render --- //
  const idGrouped = groupBy(actThinningData, "id");
  const hasReferencedStandData = newThinningData.length > 0;

  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: "max-content auto minmax(0, 1fr)",
        columnGap: 2,
        rowGap: 1,
      }}
    >
      {Object.values(idGrouped).map((el, index) => {
        const label = el[0].label;
        const data = el;
        return (
          <Fragment key={label + "-" + index}>
            <Chip
              label={label}
              variant="light"
              color="primary"
              size="small"
              disabled={disabled}
              sx={{ mt: 2, justifySelf: "stretch", justifyContent: "center" }}
            />
            <Divider orientation="vertical" flexItem sx={{ mt: 2 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                {data.map((thinning) => {
                  return (
                    <SingleThinningLine
                      key={thinning.thinningId}
                      data={thinning}
                      speciesOptions={hasReferencedStandData ? undefined : speciesOptions}
                      handleChangeThinninggPercentage={handleChangeThinninggPercentage}
                      withoutPercentage={!hasReferencedStandData}
                      deleteThinning={
                        hasReferencedStandData ? deleteThinning : handleDeleteNonReferencedSpecies
                      }
                      handleChangeSpecies={
                        hasReferencedStandData ? undefined : handleChangeSpecies
                      }
                      disabled={disabled}
                    />
                  );
                })}
              </Grid>
            </Box>
          </Fragment>
        );
      })}
    </Box>
  );
};

export default ThinningObject;

// Path: src/components/projectElements/thinningObject.tsx

// --- Helper functions --- //
type GetThinningDataFromStandData = {
  activityId: string;
  projectId: string;
  standData: MapDataStandData;
  features: MapDataFeatures;
  selectedFeature: any;
  thinningData: MapDataThinning;
  references: { id: string; label: string }[];
  removedStandIds: string[];
  getSpeciesModelType: (species: string) => string;
  getSpeciesNameAndAbbreviation: (species: string) => { name: string; abbreviation: string };
  getSpeciesColor: (species: string) => string;
  getSpecieVolume: (modelType: EstimationModels, params: any) => { vb: number };
};
function getThinningDataFromStandData({
  activityId,
  projectId,
  features,
  selectedFeature,
  standData,
  thinningData,
  references,
  removedStandIds,
  getSpeciesModelType,
  getSpeciesNameAndAbbreviation,
  getSpeciesColor,
  getSpecieVolume,
}: GetThinningDataFromStandData): ActivityThinningObject[] {
  let actThinningData: ActivityThinningObject[] = [];
  let featuresWithThinning: string[] = [];
  references.forEach((ref) => {
    const splitRef = ref.id.split("/");
    const mapId = splitRef[0];
    const featureId = splitRef[1];
    let featureMetric: number | null = null;
    // Find activity feature
    const activityFeature = features[activityId]
      ? Object.values(features[activityId]).filter((feat) => {
        return feat.properties?.originsFeatureId;
      })
      : [];
    if (activityFeature.length > 0) {
      featureMetric = activityFeature[0].properties?.originsFeatureMetric
        ? activityFeature[0].properties.originsFeatureMetric
        : null;
    }
    // If no feature metric found check for feature in map features not activity features
    if (!featureMetric) {
      if (selectedFeature && selectedFeature.feature) {
        const properties = selectedFeature.feature.properties;
        featureMetric = properties.coverage
          ? (properties.coverage / 100) * properties.featureMetric
          : null;
      }
    }
    // Check for correct id's
    if (!mapId || !featureId || !standData[mapId]) return;
    // Get thinning data
    let thinningDataObj = thinningData[mapId];
    // Find relevant stands
    const featStands = standData[mapId].filter((el) => el.featureId === featureId);
    featStands.forEach((stand) => {
      // Get relevant stand data
      const { id, species, diameter, height, treeCount } = stand;
      // If stand was removed check if it exists in thinning data and remove it from there as well
      if (removedStandIds && removedStandIds.includes(id)) return;
      // Get relevant thinning data
      const standActivityThinning = thinningDataObj
        ? Object.values(thinningDataObj).filter(
          (el) => el.standId === id && el.activityId === activityId
        )
        : [];
      const spcNameAbbr = getSpeciesNameAndAbbreviation(species);
      const spcColor = getSpeciesColor(species);
      let vol = 0;
      if (diameter && diameter > 0 && height && height > 0 && treeCount && treeCount > 0) {
        vol =
          getSpecieVolume(getSpeciesModelType(species) as EstimationModels, {
            d: diameter / 100,
            h: height,
            T: 0,
            a: 0,
          }).vb *
          treeCount *
          (featureMetric ? featureMetric : 1);
      }
      // Check for existing thinning data if none found default percentage to 30 and return
      if (
        standActivityThinning.length === 0 &&
        (activityId.startsWith("new") || !featuresWithThinning.includes(featureId))
      ) {
        actThinningData.push({
          id: ref.id,
          label: ref.label,
          mapId,
          projectId,
          featureId,
          speciesId: species,
          standId: id,
          speciesName: spcNameAbbr.name,
          speciesColor: spcColor,
          volume: vol,
          thinningPercentage: 30,
          thinningId: "new-" + generateUUID(),
        });
        return;
      }
      // Check if thinning id is present in stand thinning data
      standActivityThinning.forEach((thinning) => {
        if (thinning.activityId === activityId) {
          actThinningData.push({
            id: ref.id,
            label: ref.label,
            mapId,
            projectId,
            featureId,
            standId: id,
            speciesId: species,
            speciesName: spcNameAbbr.name,
            speciesColor: spcColor,
            volume: vol,
            thinningPercentage: thinning.percentageThinning || 30,
            thinningId: thinning.id,
            disabled: thinning.usedInProjection,
          });
          if (!featuresWithThinning.includes(featureId)) {
            featuresWithThinning.push(featureId);
          }
        }
      });
    });
  });

  return actThinningData;
}

type MapNonReferencedSpeciesToThinningDataProps = {
  nonReferencedSpecies: string[];
  references: { id: string; label: string }[];
  getSpeciesNameAndAbbreviation: (species: string) => { name: string; abbreviation: string };
  getSpeciesColor: (species: string) => string;
  translate: (key: string) => string;
};
function mapNonReferencedSpeciesToThinningData({
  nonReferencedSpecies,
  references,
  getSpeciesNameAndAbbreviation,
  getSpeciesColor,
  translate,
}: MapNonReferencedSpeciesToThinningDataProps): ActivityThinningObject[] {
  // Create label from references if any.
  let label = translate("project-activity-map-feature-references-none");
  if (references.length > 0) {
    label = references.map((ref) => ref.label).join(", ");
  }
  const data: ActivityThinningObject[] = nonReferencedSpecies.map((spc) => {
    const { name } = getSpeciesNameAndAbbreviation(spc);
    const color = getSpeciesColor(spc);
    return {
      id: "non-referenced",
      label: label,
      mapId: "",
      projectId: "",
      featureId: "",
      standId: "",
      speciesId: spc,
      speciesName: name,
      speciesColor: color,
      volume: 0,
      thinningPercentage: 0,
      thinningId: "new-" + generateUUID(),
      disabled: spc === "" ? true : false,
    };
  });
  return data;
}

function groupBy(
  items: ActivityThinningObject[],
  field: string
): Record<string, ActivityThinningObject[]> {
  return items.reduce((groups, item) => {
    //@ts-ignore
    const category = item[field];
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, ActivityThinningObject[]>);
}
